import type { DicomJson, InstanceRef, SeriesRef } from '../dicomweb';
import { tagNumber } from '../dicomweb';
import type { ViewerShell } from '../shell';
import type { Viewport } from '../viewport';
import { type DicomDataSet, elementBytes, fetchBulk } from './retrieve';
import { isMobileDevice } from '../device';
import { LruMap } from '../lru';

/**
 * DICOM overlay planes (groups 6000–601E): 1-bit graphics a modality stores beside the pixels
 * (positioning marks, ROIs). IMEXHS draws them on every image (`pixelOverlay: true`, spec section 3);
 * so does this module, on each render, in image-pixel coordinates, in white.
 * Fast mode reads the plane from the metadata's BulkDataURI; parity mode from the Part-10 file the
 * WADO-URI loader already parsed.
 */

const FIRST_GROUP = 0x6000;
const LAST_GROUP = 0x601e;
const GROUP_STEP = 2;
const OVERLAY_COLOR = [255, 255, 255, 255] as const;
const RGBA = 4;
const WADOURI_PREFIX = 'wadouri:';
/**
 * Frame bitmaps kept per plane. A 144-frame RF series with a multi-frame overlay used to paint every
 * frame's bitmap up front (seconds of main-thread work, and the tab killed on iOS); frames are now
 * painted when first shown and the least recently used dropped.
 */
const MAX_FRAME_BITMAPS = 32;
/** Instances whose overlay data stays cached (fewer on phones and tablets). */
const MAX_INSTANCES = isMobileDevice() ? 32 : 256;

interface Plane {
  group: string;
  rows: number;
  columns: number;
  /** 1-based (row, column) of the plane's top-left pixel on the image. */
  originRow: number;
  originColumn: number;
  frames: number;
  firstFrame: number;
}

interface DrawnPlane {
  plane: Plane;
  bits: Uint8Array;
  bitmaps: LruMap<number, HTMLCanvasElement>;
}

const cache = new LruMap<string, Promise<DrawnPlane[]>>(MAX_INSTANCES);

function groupHex(group: number): string {
  return group.toString(16).toUpperCase().padStart(4, '0');
}

function planesFromJson(json: DicomJson | undefined): Plane[] {
  const planes: Plane[] = [];
  for (let g = FIRST_GROUP; g <= LAST_GROUP; g += GROUP_STEP) {
    const group = groupHex(g);
    const rows = tagNumber(json, `${group}0010`);
    const columns = tagNumber(json, `${group}0011`);
    if (!rows || !columns || !json?.[`${group}3000`]) continue;
    const origin = json[`${group}0050`]?.Value as number[] | undefined;
    planes.push({
      group, rows, columns,
      originRow: Number(origin?.[0] ?? 1), originColumn: Number(origin?.[1] ?? 1),
      frames: Math.max(1, tagNumber(json, `${group}0015`, 1)), firstFrame: Math.max(1, tagNumber(json, `${group}0051`, 1)),
    });
  }
  return planes;
}

function uint16(dataSet: DicomDataSet, tag: string): number | undefined {
  const bytes = elementBytes(dataSet, tag);
  return bytes && bytes.length >= 2 ? bytes[0] | (bytes[1] << 8) : undefined;
}

function int16Pair(dataSet: DicomDataSet, tag: string): [number, number] | undefined {
  const bytes = elementBytes(dataSet, tag);
  if (!bytes || bytes.length < 4) return undefined;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return [view.getInt16(0, true), view.getInt16(2, true)];
}

function planesFromDataSet(dataSet: DicomDataSet): Array<{ plane: Plane; bits: Uint8Array }> {
  const out: Array<{ plane: Plane; bits: Uint8Array }> = [];
  for (let g = FIRST_GROUP; g <= LAST_GROUP; g += GROUP_STEP) {
    const group = groupHex(g).toLowerCase();
    const rows = uint16(dataSet, `x${group}0010`);
    const columns = uint16(dataSet, `x${group}0011`);
    const bits = elementBytes(dataSet, `x${group}3000`);
    if (!rows || !columns || !bits) continue;
    const origin = int16Pair(dataSet, `x${group}0050`) ?? [1, 1];
    out.push({
      plane: { group: group.toUpperCase(), rows, columns, originRow: origin[0], originColumn: origin[1], frames: 1, firstFrame: 1 },
      bits,
    });
  }
  return out;
}

/** One frame of a packed 1-bit plane as a white-on-transparent canvas (bits are LSB-first, row-major). */
function toBitmap(plane: Plane, bits: Uint8Array, frameIndex: number): HTMLCanvasElement {
  const pixels = plane.rows * plane.columns;
  const offset = frameIndex * pixels;
  const canvas = document.createElement('canvas');
  canvas.width = plane.columns;
  canvas.height = plane.rows;
  const context = canvas.getContext('2d');
  if (!context) return canvas;
  const image = context.createImageData(plane.columns, plane.rows);
  for (let i = 0; i < pixels; i++) {
    const bit = offset + i;
    if ((bits[bit >> 3] >> (bit & 7)) & 1) image.data.set(OVERLAY_COLOR, i * RGBA);
  }
  context.putImageData(image, 0, 0);
  return canvas;
}

function drawnPlanes(entries: Array<{ plane: Plane; bits: Uint8Array }>): DrawnPlane[] {
  return entries.map(({ plane, bits }) => ({ plane, bits, bitmaps: new LruMap<number, HTMLCanvasElement>(MAX_FRAME_BITMAPS) }));
}

/**
 * Bitmap of the plane for an image frame (1-based), painted on first use. A single-frame plane covers
 * every frame; a multi-frame plane only frames firstFrame .. firstFrame + frames - 1.
 */
export function overlayFrameIndex(plane: Pick<Plane, 'frames' | 'firstFrame'>, frame: number): number | undefined {
  if (plane.frames === 1) return 0;
  const index = frame - plane.firstFrame;
  return index >= 0 && index < plane.frames ? index : undefined;
}

function bitmapFor(drawn: DrawnPlane, frame: number): HTMLCanvasElement | undefined {
  const index = overlayFrameIndex(drawn.plane, frame);
  if (index === undefined) return undefined;
  let bitmap = drawn.bitmaps.get(index);
  if (!bitmap) {
    bitmap = toBitmap(drawn.plane, drawn.bits, index);
    drawn.bitmaps.set(index, bitmap);
  }
  return bitmap;
}

async function loadFromMetadata(shell: ViewerShell, instance: InstanceRef): Promise<DrawnPlane[]> {
  const planes = planesFromJson(instance.metadata);
  const entries: Array<{ plane: Plane; bits: Uint8Array }> = [];
  for (const plane of planes) {
    const bits = await fetchBulk(shell, instance.metadata, `${plane.group}3000`);
    if (bits) entries.push({ plane, bits });
  }
  return drawnPlanes(entries);
}

function loadFromWadoUri(imageId: string): DrawnPlane[] {
  const uri = imageId.slice(WADOURI_PREFIX.length).split('&frame=')[0];
  const manager = window.cornerstoneWADOImageLoader.wadouri.dataSetCacheManager as unknown as { get(uri: string): DicomDataSet | undefined };
  const dataSet = manager.get(uri);
  return dataSet ? drawnPlanes(planesFromDataSet(dataSet)) : [];
}

function locate(series: SeriesRef, index: number): { instance: InstanceRef; frame: number } | undefined {
  let remaining = index;
  for (const instance of series.instances) {
    if (remaining < instance.numberOfFrames) return { instance, frame: remaining + 1 };
    remaining -= instance.numberOfFrames;
  }
  return undefined;
}

function planesFor(shell: ViewerShell, viewport: Viewport, imageId: string, instance: InstanceRef): Promise<DrawnPlane[]> {
  const key = `${instance.sopInstanceUID}`;
  let pending = cache.get(key);
  if (!pending) {
    pending = imageId.startsWith(WADOURI_PREFIX)
      ? Promise.resolve(loadFromWadoUri(imageId))
      : loadFromMetadata(shell, instance);
    pending.then((planes) => { if (planes.length) window.cornerstone.updateImage(viewport.element); }).catch(() => undefined);
    cache.set(key, pending.catch((error) => {
      console.warn('[viewer] overlay planes unavailable', error);
      return [];
    }));
  }
  return cache.get(key) as Promise<DrawnPlane[]>;
}

const ready = new LruMap<string, DrawnPlane[]>(MAX_INSTANCES);

function draw(shell: ViewerShell, viewport: Viewport): void {
  const stack = viewport.currentStack;
  const index = viewport.currentIndex();
  const enabled = window.cornerstone.getEnabledElement(viewport.element);
  if (!stack || index === undefined || !enabled.image) return;
  const where = locate(stack.series, index);
  if (!where) return;
  if (!where.instance.metadata && !enabled.image.imageId.startsWith(WADOURI_PREFIX)) return;
  if (where.instance.metadata && planesFromJson(where.instance.metadata).length === 0) return;
  const planes = ready.get(where.instance.sopInstanceUID);
  if (!planes) {
    void planesFor(shell, viewport, enabled.image.imageId, where.instance).then((loaded) => ready.set(where.instance.sopInstanceUID, loaded));
    return;
  }
  const context = enabled.canvas.getContext('2d');
  if (!context || planes.length === 0) return;
  context.save();
  (window.cornerstone as unknown as { setToPixelCoordinateSystem(e: unknown, c: CanvasRenderingContext2D): void })
    .setToPixelCoordinateSystem(enabled, context);
  for (const drawn of planes) {
    const bitmap = bitmapFor(drawn, where.frame);
    if (bitmap) context.drawImage(bitmap, drawn.plane.originColumn - 1, drawn.plane.originRow - 1);
  }
  context.restore();
}

/** Number of overlay planes on the image shown in `viewport` (for tests and the metadata dialog). */
export function overlayPlaneCount(viewport: Viewport): number {
  const stack = viewport.currentStack;
  const index = viewport.currentIndex();
  const where = stack && index !== undefined ? locate(stack.series, index) : undefined;
  return where ? (ready.get(where.instance.sopInstanceUID)?.length ?? 0) : 0;
}

/** Draws every overlay plane on each render (cheap: one drawImage per plane, bitmaps cached per image). */
export function install(shell: ViewerShell): void {
  shell.on('image-rendered', ({ viewport }) => draw(shell, viewport));
  (window as unknown as { __viewerOverlayPlanes?: unknown }).__viewerOverlayPlanes = {
    count: (cell = 0) => {
      const viewport = shell.viewports()[cell];
      return viewport ? overlayPlaneCount(viewport) : 0;
    },
  };
}
